import {
  LayoutDashboard,
  Waves,
  RefreshCw,
  Bell,
  FileText,
  Settings,
  ClipboardList,
  ScanLine,
  FileSpreadsheet,
  Package,
  Utensils,
  DollarSign,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import type { UserRole } from '@/types/membership';

export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  /** Roles that can see this item. If undefined, all roles can see it. */
  roles?: UserRole[];
}

/**
 * Full navigation items - filtered by role
 */
const allNavItems: NavItem[] = [
  // Today first for technicians (their primary task)
  { name: 'Today', href: '/entry', icon: ClipboardList },
  { name: 'Overview', href: '/overview', icon: LayoutDashboard },
  { name: 'Ponds', href: '/ponds', icon: Waves },
  { name: 'Cycles', href: '/cycles', icon: RefreshCw, roles: ['supervisor', 'owner', 'operator'] },
  { name: 'Alerts', href: '/alerts', icon: Bell, roles: ['supervisor', 'owner', 'operator'] },
  { name: 'Inventory', href: '/inventory', icon: Package, roles: ['supervisor', 'owner', 'operator'] },
  { name: 'Feedings', href: '/feedings', icon: Utensils, roles: ['supervisor', 'owner', 'operator'] },
  { name: 'Harvests', href: '/harvests', icon: TrendingUp, roles: ['supervisor', 'owner', 'operator'] },
  { name: 'Financials', href: '/financials', icon: DollarSign, roles: ['supervisor', 'owner', 'operator'] },
  { name: 'OCR Import', href: '/ocr', icon: ScanLine, roles: ['operator'] },
  { name: 'Excel Import', href: '/import', icon: FileSpreadsheet, roles: ['operator'] },
  { name: 'Reports', href: '/reports', icon: FileText, roles: ['owner', 'operator'] },
  { name: 'Settings', href: '/settings', icon: Settings, roles: ['supervisor', 'owner', 'operator'] },
];

/**
 * Get navigation items for a specific role
 */
export function getNavigationForRole(role: UserRole | null): NavItem[] {
  if (!role) return [];

  return allNavItems.filter((item) => {
    // If no roles specified, visible to all
    if (!item.roles) return true;
    // Otherwise check if user's role is in the allowed list
    return item.roles.includes(role);
  });
}

/**
 * Get the default landing page for a role
 * Technicians go to Entry, supervisors/owners go to Overview
 */
export function getDefaultRoute(role: UserRole | null): string {
  if (role === 'technician') {
    return '/entry';
  }
  return '/overview';
}
